import type { StreamActivityStep } from '@/features/chat/hooks/useChat.types';
import { AnimatedThinkingLabel } from './AnimatedThinkingLabel';

/**
 * Live streaming activity timeline — hidden once text starts streaming.
 * Pure presentational leaf: renders the provided activity steps; the last
 * step animates via AnimatedThinkingLabel, earlier steps render a static dot.
 *
 * IN-PLACE STATUS SLOT: this is the dedicated EPHEMERAL status area — it lives
 * outside the transcript message rows (rendered after the messages map in
 * MessageList). The ACTIVE (last) step is keyed with a STABLE constant so
 * successive status updates
 * ("Thinking…" → "Searching…" → "Updating task…") re-render the SAME DOM node
 * IN PLACE instead of remounting. Keying the active slot by the per-step id
 * (which changes on every status) remounted the node on each update, replaying
 * the `thinking-step-enter` / `gpt-thinking-in` entry animation AND restarting
 * the AnimatedThinkingLabel shimmer/ellipsis — the flicker that made the
 * transient status read like an assistant bubble appearing then vanishing.
 * Historical (non-active) steps keep their stable per-step id.
 */
const ACTIVE_STATUS_KEY = 'active-status'

export function ThinkingTimeline({ steps }: { steps: StreamActivityStep[] }) {
  return (
    <div className="streaming-status-indicator">
      <div className="thinking-timeline" ref={(el) => { if (el) el.scrollTop = el.scrollHeight }}>
        {steps.map((step, index, arr) => {
          const isLast = index === arr.length - 1

          return (
            <div
              key={isLast ? ACTIVE_STATUS_KEY : step.id}
              className={`thinking-step thinking-step--${step.type}${isLast ? ' is-active' : ''}`}
            >
              {!isLast && (
                <span className={`thinking-step-dot${step.type === 'tool_error' ? ' thinking-step-dot--error' : ''}`} aria-hidden="true" />
              )}
              {isLast ? (
                <AnimatedThinkingLabel text={step.label} />
              ) : (
                <span className="thinking-step-label">{step.label}</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
